// The chord-change trainer's workings: which changes a song has and how hard
// each is, and a counter that listens for changes between two chords.

import { parseChord, transposeChord } from '../backing-track/chords';
import MicInput, { ChordTracker } from '../backing-track/listener';
import { getAudioContext } from './audio';
import { shapeFrets } from './strum';

// Same pair either way round: "C|G" for G → C and C → G.
export const pairKey = (a, b) => [a, b].sort().join('|');

// Shapes that are usually played as barre chords, for instruments (or
// chords) the catalogue has no diagram for.
const BARRE_LIKELY = /^(F|B|Bb|A#|C#|Db|D#|Eb|F#|Gb|G#|Ab)(m|m7|7|maj7)?$|^(Cm|Fm|Gm|C#m|D#m|Ebm|G#m|Abm|Bbm|Bm)/;

function isBarre(frets) {
    const fretted = frets.filter(f => f !== null && f > 0);
    if (fretted.length < 4) return false;
    const lowest = Math.min(...fretted);
    // One finger across several strings, from one of the bass strings.
    const held = frets.filter(f => f === lowest).length;
    return held >= 3 && (frets[0] === lowest || frets[1] === lowest);
}

const position = frets => {
    const fretted = frets.filter(f => f !== null && f > 0);
    return fretted.length ? Math.min(...fretted) : 0;
};

// Rough effort of changing between two shapes: fingers that have to move,
// barres, and moving up or down the neck.
export function changeDifficulty(a, b) {
    const fa = shapeFrets(a.chord);
    const fb = shapeFrets(b.chord);
    if (fa && fb && fa.length === fb.length) {
        let moves = 0;
        fa.forEach((x, i) => {
            const y = fb[i];
            const fx = x > 0 ? x : null;
            const fy = y > 0 ? y : null;
            if (fx !== fy) moves += fx && fy ? 1 : 0.6;
        });
        return moves + 1.5 * (isBarre(fa) + isBarre(fb)) + Math.abs(position(fa) - position(fb)) / 3;
    }
    return 2 + 1.5 * (BARRE_LIKELY.test(a.name) + BARRE_LIKELY.test(b.name));
}

export const difficultyLevel = difficulty => (difficulty >= 6 ? 3 : difficulty >= 3.5 ? 2 : 1);

// The song's chord changes, hardest first: [{ key, a, b, count, difficulty }]
// where a → b is the way round it first happens. names: the song's chords in
// order; chordsByName: the site's diagrams for the player's instrument.
export function songChanges(names, chordsByName) {
    const pairs = new Map();
    names.forEach((name, i) => {
        const previous = names[i - 1];
        if (i === 0 || previous === name || !parseChord(name) || !parseChord(previous)) return;
        const key = pairKey(previous, name);
        if (!pairs.has(key)) {
            pairs.set(key, { key, a: previous, b: name, count: 0 });
        }
        pairs.get(key).count += 1;
    });
    return Array.from(pairs.values())
        .map(pair => ({
            ...pair,
            difficulty: changeDifficulty(
                { name: pair.a, chord: chordsByName[pair.a] },
                { name: pair.b, chord: chordsByName[pair.b] },
            ),
        }))
        .sort((x, y) => y.difficulty - x.difficulty || y.count - x.count);
}

// Listens to the shared microphone and counts changes between the two
// chords: each time the one heard switches to the other. onChange receives
// { chord: 0 | 1 | null, changes, level }. capo: the fret the player's capo
// is on, which makes the chords sound that many semitones higher.
export class ChangeCounter {
    constructor(a, b, onChange, { capo = 0 } = {}) {
        this.chords = [a, b].map(name => transposeChord(parseChord(name), capo));
        // The song alternates between the two, so a change is always expected.
        this.tracker = new ChordTracker(this.chords, [0, 1, 0, 1]);
        this.onChange = onChange;
        this.changes = 0;
        this.current = null;
        this.counting = false;
    }

    async start() {
        this.mic = new MicInput(getAudioContext(), frame => this.onFrame(frame));
        // Nothing else is playing during a round, so no echo cancellation.
        await this.mic.start({ headphones: true });
    }

    onFrame({ rms, active, chroma }) {
        if (active) {
            const index = this.tracker.update(chroma);
            if (index !== null && index !== this.current) {
                if (this.current !== null && this.counting) this.changes += 1;
                this.current = index;
                this.tracker.setHint(1 - index);
            }
        }
        this.onChange({ chord: this.current, changes: this.changes, level: rms });
    }

    // Counting starts on "go"; the chord before that is the starting chord.
    go() {
        this.counting = true;
        this.changes = 0;
    }

    // Count one by hand (no microphone, or it missed one).
    add() {
        this.changes += 1;
        this.onChange({ chord: this.current, changes: this.changes, level: 0 });
    }

    stop() {
        this.counting = false;
        if (this.mic) this.mic.stop();
        this.mic = null;
    }
}
